import React, { useState } from 'react';
import { X, Calendar, Phone, Mail, MessageSquare, User } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import { updateCompanyContact } from '../../services/moderatorService';
import { CompanyContact } from '../../types';

interface EditContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  contact: CompanyContact | null;
  onContactUpdated: () => void;
}

const EditContactModal: React.FC<EditContactModalProps> = ({ isOpen, onClose, contact, onContactUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(() => ({
    contactDate: contact?.contactDate ? contact.contactDate.toDate().toISOString().split('T')[0] : '',
    contactMethod: contact?.contactMethod || 'email',
    contactPerson: contact?.contactPerson || '',
    response: contact?.response || 'pending',
    notes: contact?.notes || '',
    followUpDate: contact?.followUpDate ? contact.followUpDate.toDate().toISOString().split('T')[0] : ''
  }));

  React.useEffect(() => {
    if (contact) {
      setFormData({
        contactDate: contact.contactDate ? contact.contactDate.toDate().toISOString().split('T')[0] : '',
        contactMethod: contact.contactMethod || 'email',
        contactPerson: contact.contactPerson || '',
        response: contact.response || 'pending',
        notes: contact.notes || '',
        followUpDate: contact.followUpDate ? contact.followUpDate.toDate().toISOString().split('T')[0] : ''
      });
    }
  }, [contact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contact) return;
    try {
      setLoading(true);
      await updateCompanyContact(contact.id, {
        contactDate: Timestamp.fromDate(new Date(formData.contactDate)),
        contactMethod: formData.contactMethod,
        contactPerson: formData.contactPerson || undefined,
        response: formData.response,
        notes: formData.notes,
        followUpDate: formData.followUpDate ? Timestamp.fromDate(new Date(formData.followUpDate)) : undefined
      });
      onContactUpdated();
      onClose();
    } catch (error) {
      console.error('Error updating contact:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Modifica Contatto Azienda</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Data Contatto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Contatto *</label>
            <input type="date" className="w-full border rounded p-2" required value={formData.contactDate} onChange={e => setFormData(f => ({ ...f, contactDate: e.target.value }))} />
          </div>
          {/* Metodo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metodo *</label>
            <select className="w-full border rounded p-2" value={formData.contactMethod} onChange={e => setFormData(f => ({ ...f, contactMethod: e.target.value as any }))}>
              <option value="email">Email</option>
              <option value="phone">Telefono</option>
              <option value="linkedin">LinkedIn</option>
              <option value="letter">Lettera</option>
              <option value="direct">Diretto</option>
              <option value="other">Altro</option>
            </select>
          </div>
          {/* Persona */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Persona Contattata</label>
            <input type="text" className="w-full border rounded p-2" value={formData.contactPerson} onChange={e => setFormData(f => ({ ...f, contactPerson: e.target.value }))} />
          </div>
          {/* Risposta */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Risposta</label>
            <select className="w-full border rounded p-2" value={formData.response} onChange={e => setFormData(f => ({ ...f, response: e.target.value as any }))}>
              <option value="positive">Positiva</option>
              <option value="negative">Negativa</option>
              <option value="pending">In Attesa</option>
              <option value="no-response">Nessuna Risposta</option>
            </select>
          </div>
          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea className="w-full border rounded p-2" rows={3} value={formData.notes} onChange={e => setFormData(f => ({ ...f, notes: e.target.value }))} />
          </div>
          {/* Follow Up */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data Follow Up</label>
            <input type="date" className="w-full border rounded p-2" value={formData.followUpDate} onChange={e => setFormData(f => ({ ...f, followUpDate: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400">Annulla</button>
            <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{loading ? 'Salvataggio...' : 'Salva Modifiche'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContactModal; 